import React, { useState, useEffect } from 'react';
import { Box, Container, Tab, Tabs, Grid, TextField, InputAdornment, FormControl, InputLabel, Select, MenuItem, Stack, SelectChangeEvent } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Search as SearchIcon } from '@mui/icons-material';
import dayjs, { Dayjs } from 'dayjs';
import UnifiedFeedbackPage from './UnifiedFeedbackPage';
import ComplaintManagementPage from './ComplaintManagementPage';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// Define filter props interface
export interface FilterProps {
  searchTerm: string;
  platformFilter: string;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  sentimentFilter: string;
  categoryFilter: string;
  priorityFilter: string;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`feedback-tabpanel-${index}`}
      aria-labelledby={`feedback-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `feedback-tab-${index}`,
    'aria-controls': `feedback-tabpanel-${index}`,
  };
}

const FeedbackDashboard = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loadedTabs, setLoadedTabs] = useState<number[]>([0]); // Keep track of loaded tabs
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [platformFilter, setPlatformFilter] = useState<string>('all');
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [sentimentFilter, setSentimentFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    if (!loadedTabs.includes(newValue)) {
      setLoadedTabs([...loadedTabs, newValue]);
    }
  };
  
  // Filter handlers
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };
  
  const handlePlatformChange = (event: SelectChangeEvent<string>) => {
    setPlatformFilter(event.target.value);
  };
  
  const handleSentimentChange = (event: SelectChangeEvent<string>) => {
    setSentimentFilter(event.target.value);
  };
  
  const handleCategoryChange = (event: SelectChangeEvent<string>) => {
    setCategoryFilter(event.target.value);
  };
  
  const handlePriorityChange = (event: SelectChangeEvent<string>) => {
    setPriorityFilter(event.target.value);
  };

  // Prepare filter props to pass down to child components
  const filterProps: FilterProps = {
    searchTerm,
    platformFilter,
    startDate,
    endDate,
    sentimentFilter,
    categoryFilter,
    priorityFilter,
  };

  return (
    <Container maxWidth={false}>
 
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          aria-label="feedback management tabs"
        >
          <Tab label="All Feedback" {...a11yProps(0)} />
          <Tab label="Need Action" {...a11yProps(1)} />
        </Tabs>
      </Box>
      
      <TabPanel value={activeTab} index={0}>
        {loadedTabs.includes(0) && <UnifiedFeedbackPage filterProps={filterProps} />}
      </TabPanel>
      
      <TabPanel value={activeTab} index={1}>
        {loadedTabs.includes(1) && <ComplaintManagementPage filterProps={filterProps} />}
      </TabPanel>
    </Container>
  );
};

export default FeedbackDashboard; 